"use client";

import { useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import { HeadlineAccent } from "@/components/marketing/HeadlineAccent";
import { LeadMagnet } from "./LeadMagnet";

const STORAGE_KEY = "sa-exit-intent-dismissed";
const REPORT_PATH = "/adult-singing-confidence-report";
const DESKTOP_DELAY_MS = 45_000;
const MOBILE_DELAY_MS = 55_000;
const SCROLL_THRESHOLD = 0.45;

type ExitIntentOffer = {
  eyebrow: string;
  heading: string;
  body: string;
  title: string;
  subtitle: string;
  benefits: string[];
  ctaLabel: string;
  context: string;
  source: string;
  leadType?: string;
  trackWarmupRequest?: boolean;
  languageLabel: string;
  metadata?: Record<string, string>;
  reassuranceText?: string;
  successMessage?: string;
};

const warmupsOffer: ExitIntentOffer = {
  eyebrow: "Before you go",
  heading: "Get weekly singing tip videos",
  body: "Short, practical videos to help you understand your voice, reduce tension and know what to practise next.",
  title: "Weekly singing tip videos",
  subtitle: "Useful singing tips and occasional updates, sent by email.",
  benefits: [],
  ctaLabel: "Send Me Weekly Singing Tips",
  context: "exit-intent",
  source: "weekly_singing_tips",
  leadType: "newsletter",
  trackWarmupRequest: false,
  languageLabel: "Preferred language",
  metadata: {
    newsletter: "weekly_singing_tips",
    source_section: "exit_intent_popup",
    source_cta: "weekly_singing_tip_videos",
  },
  reassuranceText: "We'll send useful singing tips and occasional updates. Unsubscribe anytime.",
  successMessage: "Sent. Check your inbox for your first singing tip.",
};

const reportChecklistOffer: ExitIntentOffer = {
  eyebrow: "Free checklist",
  heading: "Before you go - find your likely voice blocker",
  body: "Get the free Voice Blocker Checklist and a gentle next-step guide for adults who feel held back by nerves, tension, pitch worries, The Bridge, or unclear feedback.",
  title: "Voice Blocker Checklist",
  subtitle: "Send yourself the printable checklist link and use it when you want a calmer first step.",
  benefits: [
    "Notice whether your blocker is technical, confidence-based, or mixed",
    "Use it before a choir visit, audition, rehearsal, or evaluation",
    "Get a calmer first step without needing to sing for anyone",
  ],
  ctaLabel: "Send me the checklist link",
  context: "exit-intent-report-checklist",
  source: "lead_magnet",
  languageLabel: "Preferred language",
  metadata: {
    lead_magnet: "voice_blocker_checklist",
    source_page: "adult_singing_confidence_report",
    source_section: "exit_intent_popup",
    source_cta: "voice_blocker_checklist",
  },
  reassuranceText: "No spam. Unsubscribe anytime.",
  successMessage: "Sent. Check your inbox for the checklist link.",
};

function getCurrentOffer() {
  if (typeof window !== "undefined" && window.location.pathname === REPORT_PATH) {
    return reportChecklistOffer;
  }
  return warmupsOffer;
}

function hasScrolledEnough() {
  const scrollableHeight = document.documentElement.scrollHeight - window.innerHeight;
  if (scrollableHeight <= 0) return false;
  return window.scrollY / scrollableHeight >= SCROLL_THRESHOLD;
}

function renderOfferHeading(offer: ExitIntentOffer) {
  if (offer === warmupsOffer) {
    return (
      <>
        Get weekly <HeadlineAccent>singing tip</HeadlineAccent> videos
      </>
    );
  }

  if (offer === reportChecklistOffer) {
    return (
      <>
        Before you go - find your likely <HeadlineAccent>voice blocker</HeadlineAccent>
      </>
    );
  }

  return offer.heading;
}

const BOOKING_PATHS = ["/local-lessons", "/singing-lessons-ely", "/singing-lessons-bury-st-edmunds", "/contact", "/book", "/buy", "/checkout", "/portal"];

export function ExitIntentModal() {
  const pathname = usePathname() ?? "";
  const isBookingPage = pathname === "/" || BOOKING_PATHS.some((path) => pathname === path || pathname.startsWith(`${path}/`));
  const [open, setOpen] = useState(false);
  const [offer, setOffer] = useState<ExitIntentOffer>(warmupsOffer);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (typeof window === "undefined" || isBookingPage) return;
    if (sessionStorage.getItem(STORAGE_KEY)) return;
    let engagementTimerReady = false;
    const isDesktop = window.innerWidth >= 1024;
    const timer = window.setTimeout(
      () => {
        engagementTimerReady = true;
        if (hasScrolledEnough()) {
          showModal();
        }
      },
      isDesktop ? DESKTOP_DELAY_MS : MOBILE_DELAY_MS,
    );

    function showModal() {
      if (sessionStorage.getItem(STORAGE_KEY)) return;
      if (document.body.dataset.purchaseFlowActive === "true") return;
      setOffer(getCurrentOffer());
      sessionStorage.setItem(STORAGE_KEY, "shown");
      setOpen(true);
      window.removeEventListener("mouseout", handleMouseLeave);
      window.removeEventListener("scroll", handleScroll);
      window.clearTimeout(timer);
    }

    function handleMouseLeave(event: MouseEvent) {
      if (!isDesktop) return;
      if (event.clientY > 10) return;
      showModal();
    }

    function handleScroll() {
      if (!engagementTimerReady) return;
      if (!hasScrolledEnough()) return;
      showModal();
    }

    window.addEventListener("mouseout", handleMouseLeave);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("mouseout", handleMouseLeave);
      window.removeEventListener("scroll", handleScroll);
      window.clearTimeout(timer);
    };
  }, [isBookingPage]);

  useEffect(() => {
    function handlePurchaseFlow(event: Event) {
      const active = (event as CustomEvent<{ active?: boolean }>).detail?.active === true;
      if (active) setOpen(false);
    }
    window.addEventListener("sa:purchase-flow", handlePurchaseFlow);
    return () => window.removeEventListener("sa:purchase-flow", handlePurchaseFlow);
  }, []);

  useEffect(() => {
    if (!open) return;
    closeButtonRef.current?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setOpen(false);
      }
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open]);

  if (!open || isBookingPage) return null;

  return (
    <div className="fixed inset-x-2 bottom-2 z-[100] mx-auto w-auto max-w-[520px] sm:inset-x-3 sm:bottom-3 sm:w-[min(520px,calc(100vw-1.5rem))] lg:inset-x-auto lg:bottom-6 lg:right-6">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="exit-intent-title"
        className="pointer-events-auto relative max-h-[min(82dvh,calc(100dvh-1rem))] w-full overflow-y-auto rounded-[18px] border border-[#e4dccd] bg-[#fffaf2] p-4 text-[#13211f] shadow-[0_28px_78px_rgba(58,45,25,0.2)] ring-1 ring-white/70 backdrop-blur-md sm:max-h-[calc(100vh-1.5rem)] sm:rounded-[24px] sm:p-6 lg:rounded-[28px]"
      >
        <button
          ref={closeButtonRef}
          type="button"
          onClick={() => setOpen(false)}
          className="sticky top-0 z-10 ml-auto -mr-1 -mt-1 flex h-11 w-11 items-center justify-center rounded-full border border-[#d9cdb9] bg-[#fffdf8] text-base text-[#5f6a65] shadow-[0_10px_24px_rgba(58,45,25,0.12)] transition hover:border-[#c99422] hover:text-[#0b332d] sm:absolute sm:right-4 sm:top-4 sm:m-0 sm:h-8 sm:w-8 sm:bg-[#fffdf8]/90 sm:text-sm sm:shadow-none"
          aria-label="Close exit intent modal"
        >
          x
        </button>
        <p className="flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.28em] text-[#a66f16]">
          {offer.eyebrow}
          <span className="hidden h-px w-14 bg-[#c99a35]/55 sm:block" aria-hidden="true" />
        </p>
        <h3
          id="exit-intent-title"
          className="mt-3 font-serif text-[2rem] font-semibold leading-[1] tracking-tight text-[#0b332d] sm:max-w-[92%] sm:text-[3.1rem]"
        >
          {renderOfferHeading(offer)}
        </h3>
        <p className="mt-4 text-sm leading-relaxed text-[#48544f] sm:text-base">{offer.body}</p>
        {offer.benefits.length > 0 ? (
          <ul className="mt-4 grid gap-2 text-sm leading-relaxed text-[#48544f]">
            {offer.benefits.map((benefit) => (
              <li key={benefit} className="flex gap-2">
                <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#c99422]" aria-hidden="true" />
                <span>{benefit}</span>
              </li>
            ))}
          </ul>
        ) : null}
        <LeadMagnet
          className="mt-5 min-w-0 !border-[#e4dccd] !bg-[#fffdf8]/95 p-3 !text-[#13211f] shadow-[0_16px_42px_rgba(49,39,24,0.06)] ring-1 ring-white/70 sm:p-5 [&_a]:!text-[#a66f16] [&_button[type=submit]]:!col-span-1 [&_button[type=submit]]:!min-h-12 [&_button[type=submit]]:!w-full [&_button[type=submit]]:!rounded-[18px] [&_button[type=submit]]:!border [&_button[type=submit]]:!border-[#b9821d]/50 [&_button[type=submit]]:!bg-[linear-gradient(135deg,#f4d982_0%,#d9a43a_48%,#a96f18_100%)] [&_button[type=submit]]:!text-[#102f2a] [&_button[type=submit]]:!shadow-[0_18px_38px_rgba(168,111,18,0.22),inset_0_1px_0_rgba(255,255,255,0.55)] [&_button[type=submit]]:whitespace-normal [&_form>*]:min-w-0 [&_form]:!mt-4 [&_form]:!gap-2 [&_h3]:!text-2xl [&_h3]:!text-[#0b332d] [&_input]:min-w-0 [&_input]:!min-h-11 [&_input]:!border-[#e4dccd] [&_input]:!bg-[#fffaf2] [&_input]:!text-[#13211f] [&_input::placeholder]:!text-[#8a918d] [&_label]:!text-[#6a706c] [&_p]:!text-[#48544f] [&_select]:min-w-0 [&_select]:!min-h-11 [&_select]:!border-[#e4dccd] [&_select]:!bg-[#fffaf2] [&_select]:!text-[#13211f] [&_span]:!border-[#d8c7aa] [&_span]:!text-[#384541] sm:[&_button[type=submit]]:!col-span-2 sm:[&_form]:!grid-cols-[minmax(0,1fr)_9rem] lg:[&_button[type=submit]]:!col-span-2 lg:[&_form]:!grid-cols-[minmax(0,1fr)_9rem]"
          eyebrow="Send by email"
          title={offer.title}
          subtitle={offer.subtitle}
          ctaLabel={offer.ctaLabel}
          emailPlaceholder="Enter your email"
          context={offer.context}
          source={offer.source}
          leadType={offer.leadType}
          trackWarmupRequest={offer.trackWarmupRequest}
          languageLabel={offer.languageLabel}
          metadata={offer.metadata}
          reassuranceText={offer.reassuranceText}
          successMessage={offer.successMessage}
          idBase={`exit-intent-${offer.context}`}
          tone="premium"
          onSuccess={() => sessionStorage.setItem(STORAGE_KEY, "signed-up")}
        />
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="mt-4 text-sm font-medium text-[#5f6a65] underline-offset-4 transition hover:text-[#0b332d] hover:underline"
        >
          No thanks, I&apos;ll keep reading
        </button>
      </div>
    </div>
  );
}
